import { handPlaces } from "./hand";
/** Card `i` goes to `DEAL_ORDER[i % 4]` (design: top, left, right, you); a round's four fly together. */
export const DEAL_ORDER = ["seatTop", "seatLeft", "seatRight", "seatSelf"];
/** The handoff's defaults. */
export const FULL_DEAL = { charge: 420, step: 24, fly: 420 };
/** Baloot's rest after the call (owner, 11 Sept): no charge, the same flight. */
export const REST_DEAL = { charge: 0, step: 24, fly: 420 };
/** The deal is over this long after the last card lands. */
export const DEAL_SETTLE_MS = 250;
/** A pod's turn as a card vanishes into it (design): top upside down, sides sideways. */
const POD_TURN = {
    seatTop: 180,
    seatLeft: 90,
    seatRight: -90,
    seatSelf: 0,
};
/** Where the deck sits: the middle of the trick area, the table's centre. */
export function deckCentre(canvas) {
    const r = canvas.elements.trick;
    return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}
/** The centre of a seat's circle. */
export function podCentre(canvas, slot) {
    const r = canvas.elements[slot];
    return { x: r.x + r.w / 2, y: r.y + canvas.pod.size / 2 };
}
/** The end of a flight: into a pod, or onto its card's place in your hand. */
function target(canvas, slot, handIndex, places) {
    const centre = deckCentre(canvas);
    if (handIndex === null) {
        const pod = podCentre(canvas, slot);
        return { x: pod.x - centre.x, y: pod.y - centre.y };
    }
    const hand = canvas.elements.hand;
    const place = places[handIndex];
    return {
        x: hand.x + (place?.x ?? hand.w / 2) - centre.x,
        y: hand.y + (place?.y ?? hand.h) - centre.y,
    };
}
/** 55% of the way, pushed sideways so a seat's cards fan out (design: (j − 6) · 10 for 13 cards). */
function midpoint(to, spread) {
    const length = Math.hypot(to.x, to.y) || 1;
    return {
        x: to.x * 0.55 - (to.y / length) * spread,
        y: to.y * 0.55 + (to.x / length) * spread,
    };
}
/** Two full turns on the way: left and you one way, top and right the other (design). */
function spinOf(slot) {
    return slot === "seatLeft" || slot === "seatSelf" ? -720 : 720;
}
/** Your card lands on its place in your hand; everything else vanishes into a pod. */
function handIndexOf(request, slot, round) {
    if (slot !== "seatSelf")
        return null;
    return request.landing?.[round] ?? null;
}
/** How a card ends: at hand size on its tilt in your hand, or turned with the pod and shrunk to nothing. */
function ending(frame, slot, handIndex, spin) {
    if (handIndex !== null)
        return {
            endRotate: frame.places[handIndex]?.rotate ?? 0,
            midScale: frame.handScale * 0.8,
            endScale: frame.handScale,
        };
    return { endRotate: POD_TURN[slot] + spin, midScale: 0.75, endScale: 0.08 };
}
function flightFor(frame, i) {
    const { canvas, request } = frame;
    const slot = DEAL_ORDER[i % 4] ?? "seatTop";
    const round = Math.floor(i / 4);
    const handIndex = handIndexOf(request, slot, round);
    const mine = handIndex !== null;
    const to = target(canvas, slot, handIndex, frame.places);
    const spin = spinOf(slot);
    const spread = mine ? 0 : (round - (request.cardsEach - 1) / 2) * 10 * frame.k;
    return {
        id: `deal-${i}`,
        slot,
        round,
        delay: request.timing.charge + round * request.timing.step,
        mid: midpoint(to, spread),
        to,
        midRotate: spin * 0.55,
        ...ending(frame, slot, handIndex, spin),
        handIndex,
    };
}
/** Every card of the deal, round by round; yours land face up in your hand, the others vanish into pods. */
export function dealFlights(canvas, request) {
    const frame = {
        canvas,
        request,
        places: handPlaces(canvas, request.handCount),
        k: canvas.trickCard.w / 72,
        handScale: canvas.card.w / canvas.trickCard.w,
    };
    return Array.from({ length: request.cardsEach * 4 }, (_, i) => flightFor(frame, i));
}
/** From the first frame until the deal is over. */
export function dealDuration(request) {
    const { charge, step, fly } = request.timing;
    return charge + Math.max(0, request.cardsEach - 1) * step + fly + DEAL_SETTLE_MS;
}
//# sourceMappingURL=deal.js.map